import React, {useRef, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import YouTube from 'react-youtube'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PlayIcon} from '../icons'
import {remCalc} from '../../theme/_utils'
import LoadingSpinner from '../loading-spinner'
import {BRAND_KEY} from '../../brand'
import {useIntl} from 'react-intl'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {video_gtm_event} from '../../custom-api/layers/default'

/**
 * Extracts the YouTube video ID from a given URL.
 *
 * @param {string} url - The URL of the YouTube video.
 * @returns {string|null} The YouTube video ID if found, otherwise null.
 */
const getYoutubeVideoId = (url) => {
    // If the input is the ID itself, return the input
    if (typeof url === 'string' && url.length === 11) {
        return url
    }

    var regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\\&v=)([^#\\&\\?]*).*/
    var match = url?.match(regExp)
    if (match && match[2].length == 11) {
        return match[2]
    } else {
        return null
    }
}

/**
 * A React component that renders a YouTube iframe.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the video.
 * @param {string} props.videoSrc - The source URL of the YouTube video.
 * @param {string|Object} [props.height] - The height of the iframe.
 * @returns {JSX.Element} The rendered YouTube iframe component.
 */
const YoutubeIframe = ({
    title,
    videoSrc,
    thumbnail = null,
    onPause,
    onPlay,
    isVariantStyle,
    enableInteractionHandlers = false,
    contentType = '',
}) => {
    const YoutubeVideoId = getYoutubeVideoId(videoSrc)
    const ref = useRef()
    const [dimensions, setDimensions] = useState({loaded: false})
    const [showVideo, setShowVideo] = useState(thumbnail ? false : true)
    const {formatMessage} = useIntl()

    const isKenwood = BRAND_KEY === 'kenwood'

    React.useEffect(() => {
        let {height} = ref.current.getBoundingClientRect()
        setDimensions({height, loaded: true})
    }, [])

    const opts = {
        height: dimensions.height,
        width: '100%',
        playerVars: {
            autoplay: 0,
        },
    }

    const {addLayer} = useGoogleTagManager()
    const addGTMVideoEvent = (event) => {
        var videoStateToGTM = null
        switch (event.data) {
            case YouTube.PlayerState.PLAYING:
                videoStateToGTM = 'video-play'
                break
            case YouTube.PlayerState.PAUSED:
                videoStateToGTM = 'video-pause'
                break
            case YouTube.PlayerState.ENDED:
                videoStateToGTM = 'video-complete'
                break
            default:
                break
        }

        if (videoStateToGTM) {
            addLayer(
                video_gtm_event.bind(this, {
                    event: 'video',
                    eventCategory: 'engagement',
                    eventAction: videoStateToGTM,
                    videoTitle: event.target?.videoTitle || '',
                    videoId: YoutubeVideoId || '',
                    videoUrl: videoSrc || '',
                    howTo: contentType === 'howto' ? 'true' : '',
                }),
            )
        }
    }

    const handleStateChange = (event) => {
        if (
            enableInteractionHandlers &&
            event.data === YouTube.PlayerState.PLAYING &&
            onPlay &&
            isVariantStyle
        ) {
            onPlay()
        }
        if (enableInteractionHandlers && event.data === YouTube.PlayerState.PAUSED && onPause) {
            onPause()
        }

        addGTMVideoEvent(event)
    }

    useEffect(() => {
        function updateHeight() {
            if (ref.current) {
                let {height} = ref.current.getBoundingClientRect()
                setDimensions({height, loaded: true})
            }
        }
        updateHeight()
        window.addEventListener('resize', updateHeight)
        return () => window.removeEventListener('resize', updateHeight)
    }, [])

    useEffect(() => {
        setShowVideo(!thumbnail)
    }, [thumbnail])

    return (
        <Box
            ref={ref}
            w="100%"
            h={isVariantStyle ? {base: '100vw', md: '100%'} : '100%'}
            data-testid="video-container"
        >
            {!showVideo && thumbnail && (
                <Box
                    as="button"
                    onClick={() => setShowVideo(true)}
                    aria-label={formatMessage({
                        id: 'youtube_video.play_button.aria_label',
                        defaultMessage: 'Play video',
                    })}
                    style={{
                        backgroundImage: `url(${thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    <PlayIcon
                        height={remCalc(40)}
                        width={remCalc(40)}
                        fill="background.default"
                        color={isKenwood ? 'background.alternative' : 'currentColor'}
                    />
                </Box>
            )}
            {!dimensions.loaded && !showVideo && (
                <LoadingSpinner data-testid="loading-spinner-video" />
            )}
            {dimensions.loaded && showVideo && (
                <YouTube
                    data-testid="youtube-video"
                    title={title}
                    videoId={YoutubeVideoId}
                    opts={opts}
                    onStateChange={handleStateChange}
                />
            )}
        </Box>
    )
}

YoutubeIframe.propTypes = {
    title: PropTypes.string,
    videoSrc: PropTypes.string,
    thumbnail: PropTypes.string,
    onBlur: PropTypes.func,
    onPause: PropTypes.func,
    onPlay: PropTypes.func,
    enableInteractionHandlers: PropTypes.bool,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isVariantStyle: PropTypes.bool,
    contentType: PropTypes.string,
}

export default YoutubeIframe
